import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import Big from 'big.js';
import LazyTooltip from 'components/LazyTooltip';
import MoneyDisplay from 'components/util/MoneyDisplay';
import LabelledData from 'pages/timecards/LabelledData';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { InvoiceExpense } from 'types/invoice-details';
import { InvoiceDetailsParameterizedTracker } from '../../InvoiceDetailsChangeTrackers';
import { CALCULATION, CALCULATION_RESULT, CALCULATION_STEP, CALCULATION_STEP_BOLD } from '../../InvoiceItemRow';

type Props = {
    currency: string;
    invoiceExpense: InvoiceExpense;
    isPaymentCurrencyMode?: boolean;
};

const ExpenseDetailedCalculations = ({
    invoiceExpense,
    isPaymentCurrencyMode,
    currency,
}: {
    invoiceExpense: InvoiceExpense;
    isPaymentCurrencyMode?: boolean;
    currency: string;
}) => {
    const { calculationLog } = invoiceExpense;
    const detailLog = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;
    const initialAmount = Math.round((detailLog?.initialAmount ?? 0) * 100) / 100;
    const matterCashAdjustmentAmount = Math.round((detailLog?.expenseMatterAdjustmentAmount ?? 0) * 100) / 100;
    const amountAfterMatterCashAdjustments =
        Math.round((detailLog?.expenseAmountBeforeMatterDiscount ?? 0) * 100) / 100;
    const matterDiscountRate = Math.round((calculationLog?.matterDiscountRate ?? 0) * 100) / 100;
    const matterDiscountAdjustmentAmount =
        Math.round((detailLog?.expenseMatterDiscountAdjustmentAmount ?? 0) * 100) / 100;
    const finalAmount = Math.round((detailLog?.finalAmount ?? 0) * 100) / 100;

    return (
        <div className={CALCULATION}>
            <p className="text-body1">
                <FormattedMessage id="invoices.details.table.initial-amount" />
            </p>
            <div className={CALCULATION_STEP}>
                <LabelledData label="invoices.details.table.amount">{initialAmount.toFixed(2)}</LabelledData>
            </div>
            <p className="text-body1 font-semibold">
                <FormattedMessage id="invoices.details.table.amount-after-matter-cash-adjustments" />
            </p>
            <div className={CALCULATION_STEP_BOLD}>
                <LabelledData label="invoices.details.table.initial-amount">{initialAmount.toFixed(2)}</LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.matter-cash-adjustments">
                    {matterCashAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>=</div>
                <div>{amountAfterMatterCashAdjustments.toFixed(2)}</div>
            </div>
            <p className="text-body1">
                <FormattedMessage id="invoices.details.table.matter-discount" />
            </p>
            <div className={CALCULATION_STEP}>
                <LabelledData label="invoices.details.table.amount-after-matter-cash-adjustments">
                    {amountAfterMatterCashAdjustments.toFixed(2)}
                </LabelledData>
                <div>*</div>
                <LabelledData label="invoices.details.table.matter-discount-rate">
                    {`-${matterDiscountRate}`}
                </LabelledData>
                <div>=</div>
                <div>{matterDiscountAdjustmentAmount.toFixed(2)}</div>
            </div>
            <p className="text-h5">
                <FormattedMessage id="invoices.details.table.final-amount" />
            </p>
            <div className={CALCULATION_RESULT}>
                <LabelledData label="invoices.details.table.initial-amount">{initialAmount.toFixed(2)}</LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.matter-cash-adjustments">
                    {matterCashAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.matter-discount">
                    {matterDiscountAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>=</div>
                <div>
                    <MoneyDisplay amount={finalAmount} currency={currency} />
                </div>
            </div>
        </div>
    );
};

export const TotalAmountDisplay = ({ currency, invoiceExpense, isPaymentCurrencyMode }: Props) => {
    const { calculationLog } = invoiceExpense;
    const log = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;

    const currentAmount = Math.round((log?.finalAmount ?? 0) * 100) / 100;
    const amountBeforeAdjustment = Math.round((log?.initialAmount ?? 0) * 100) / 100;
    const isTooltipEnabled = currentAmount !== amountBeforeAdjustment;
    const displayedBeforeAdjustment = useMemo(
        () => new Big(amountBeforeAdjustment).toFixed(2),
        [amountBeforeAdjustment]
    );

    return (
        <div className="opacity-decreasing-when-disabled flex w-full flex-col">
            <div className="flex w-full justify-between">
                <InvoiceDetailsParameterizedTracker
                    currentParam={currentAmount}
                    storedParam={amountBeforeAdjustment}
                    displayValue={`${displayedBeforeAdjustment} ${currency}`}
                />
                {isTooltipEnabled ? (
                    <LazyTooltip
                        contentClassName="w-[600px] max-w-[600px] rounded-[7px] text-[11px] new:text-caption"
                        title={
                            <ExpenseDetailedCalculations
                                invoiceExpense={invoiceExpense}
                                isPaymentCurrencyMode={isPaymentCurrencyMode}
                                currency={currency}
                            />
                        }
                    >
                        <InfoOutlinedIcon className="text-grey-400 h-[1.2rem]" />
                    </LazyTooltip>
                ) : null}
            </div>
            <MoneyDisplay amount={currentAmount} currency={currency} />
        </div>
    );
};
